import { useEffect, useState } from 'react';
import {
  applyUpdate, checkForUpdate, CURRENT_VERSION, updatesSupported,
  type Available,
} from '../update/appUpdate';
import {
  disableNotifications, enableNotifications, notificationsEnabled,
} from '../push/notificationSwitch';

interface Props {
  deviceId: string;
  onClose: () => void;
}

/** Версия — это хеш коммита; целиком он занимает полстроки и ничего не добавляет. */
function short(version: string): string {
  return version.length > 12 ? version.slice(0, 7) : version;
}

type State =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'current' }
  | { kind: 'available'; update: Available }
  | { kind: 'applying' }
  | { kind: 'error'; message: string };

/**
 * Обновление приложения по нажатию.
 *
 * Само ничего не делает намеренно: подменить интерфейс без спроса посреди
 * переписки — последнее, чего ждут от мессенджера. Человек нажимает
 * «проверить», видит, что нашлось, и решает сам.
 */
export function UpdatePanel({ deviceId, onClose }: Props) {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [notify, setNotify] = useState(notificationsEnabled());
  const [notifyError, setNotifyError] = useState<string | null>(null);

  async function toggleNotifications(enabled: boolean) {
    setNotifyError(null);
    try {
      if (enabled) {
        enableNotifications();
        setNotify(true);
      } else {
        await disableNotifications(deviceId);
        setNotify(false);
      }
    } catch {
      setNotifyError('Не удалось изменить — нет связи с сервером');
    }
  }

  // Проверяем сразу при открытии: человек пришёл сюда именно за этим,
  // лишнее нажатие ничего не добавляет. Установка по-прежнему только по кнопке.
  useEffect(() => {
    void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function check() {
    setState({ kind: 'checking' });
    const result = await checkForUpdate();
    switch (result.status) {
      case 'available': setState({ kind: 'available', update: result.update }); break;
      case 'current': setState({ kind: 'current' }); break;
      case 'error': setState({ kind: 'error', message: result.message }); break;
      case 'unsupported': setState({ kind: 'current' }); break;
    }
  }

  async function apply(update: Available) {
    setState({ kind: 'applying' });
    try {
      // После успешной замены приложение перезапускается само —
      // возврата из этого вызова, как правило, уже не будет
      await applyUpdate(update);
    } catch (e) {
      setState({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Не удалось установить обновление',
      });
    }
  }

  return (
    <div className="panel">
      <div className="section-title" style={{ padding: '2px 0 6px' }}>Уведомления</div>

      {/*
        Выключатель именно для этого устройства. У кого есть и приложение,
        и браузер, о каждом сообщении приходило по два уведомления — причём
        браузерное даже при закрытой вкладке: Web Push работает без страницы.
      */}
      <label className="check">
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => void toggleNotifications(e.target.checked)}
        />
        <span>Присылать сюда уведомления</span>
      </label>
      <p className="panel-hint">
        Выключение действует только на это устройство. Если пользуетесь
        и приложением, и браузером — оставьте там, где удобнее получать.
      </p>
      {!notify && (
        <p className="panel-hint">
          Чтобы включить обратно, отметьте галочку и перезапустите приложение
          или обновите страницу — подписка оформится заново.
        </p>
      )}
      {notifyError && <p className="error">{notifyError}</p>}

      <div className="section-title" style={{ padding: '14px 0 6px' }}>Обновление</div>

      <p className="panel-hint">
        Версия: <code>{short(CURRENT_VERSION)}</code>
      </p>

      {!updatesSupported() && (
        <p className="panel-hint">
          В браузере обновляться не нужно: достаточно перезагрузить страницу.
        </p>
      )}

      {state.kind === 'current' && (
        <p className="panel-hint">Установлена последняя версия.</p>
      )}

      {state.kind === 'available' && (
        <>
          <p className="panel-hint">
            Доступна версия <code>{short(state.update.version)}</code>
            {state.update.size > 0 && `, ${Math.round(state.update.size / 1024)} КБ`}.
            Приложение перезапустится само.
          </p>
          <div className="panel-actions">
            <button type="button" onClick={() => void apply(state.update)}>
              Обновить и перезапустить
            </button>
          </div>
        </>
      )}

      {state.kind === 'applying' && (
        <p className="panel-hint">Скачиваем и устанавливаем…</p>
      )}

      {state.kind === 'error' && <p className="error">{state.message}</p>}

      <div className="panel-actions">
        {updatesSupported() && state.kind !== 'applying' && (
          <button type="button" className="ghost" onClick={() => void check()}>
            {state.kind === 'checking' ? 'Проверяем…' : 'Проверить обновления'}
          </button>
        )}
        <button type="button" className="ghost" onClick={onClose}>Закрыть</button>
      </div>
    </div>
  );
}
